/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Home as HomeIcon, Wallet, Headphones, User } from 'lucide-react';
import PageLoadingState from './components/shared/PageLoadingState';
import { useAppConfig } from './context/AppConfigContext';
import { useAuthUser } from './context/AuthUserContext';
import { fetchCustomerServiceUnreadCount } from './services/customerServiceUnread';
import {
  consumeCustomerServiceBadgeOnce,
  ensureCustomerServiceBadge,
  markCustomerServiceBadgeRead,
} from './services/customerServiceBadge';

const Home = lazy(() => import('./pages/Home'));
const Repayment = lazy(() => import('./pages/Repayment'));
const CustomerService = lazy(() => import('./pages/CustomerService'));
const UserCenter = lazy(() => import('./pages/UserCenter'));
const LoanRecords = lazy(() => import('./pages/LoanRecords'));
const LoanDetails = lazy(() => import('./pages/LoanDetails'));
const RepaymentDetail = lazy(() => import('./pages/RepaymentDetail'));
const Contract = lazy(() => import('./pages/Contract'));
const Profile = lazy(() => import('./pages/Profile'));
const OverdueImpact = lazy(() => import('./pages/OverdueImpact'));
const UploadProof = lazy(() => import('./pages/UploadProof'));
const DeleteAccount = lazy(() => import('./pages/DeleteAccount'));
const SettlementCertificate = lazy(() => import('./pages/SettlementCertificate'));
const Login = lazy(() => import('./pages/Login'));
const UserAgreement = lazy(() => import('./pages/UserAgreement'));

const NavItem = ({
  icon: Icon,
  label,
  path,
  active,
  badgeCount = 0,
}: {
  icon: any;
  label: string;
  path: string;
  active: boolean;
  badgeCount?: number;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = () => {
    if (location.pathname === path) return;
    navigate(path, { state: { from: location.pathname } });
  };

  return (
    <button
      onClick={handleClick}
      className={`nav-item relative flex h-full flex-1 flex-col items-center justify-center overflow-visible ${active ? 'nav-item-active' : 'nav-item-idle'}`}
    >
      {badgeCount > 0 ? (
        <span className="nav-badge">
          {badgeCount}
        </span>
      ) : null}
      <span className={`nav-icon relative ${active ? 'nav-icon-active' : ''}`}>
        <Icon size={18} className={active ? 'fill-current/10' : ''} />
      </span>
      <span className="nav-label text-[10px] mt-1 font-medium">{label}</span>
    </button>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const { user } = useAuthUser();
  const showNav = ['/', '/repayment', '/user'].includes(location.pathname);
  const mainRef = useRef<HTMLElement | null>(null);
  const [customerServiceBadgeCount, setCustomerServiceBadgeCount] = useState(0);
  const [lastActualUnreadCount, setLastActualUnreadCount] = useState(0);

  const resolveFallbackBadgeCount = (phone: string, visitingCustomerService: boolean) => {
    if (!phone) return 0;
    if (visitingCustomerService) {
      markCustomerServiceBadgeRead(phone);
      return 0;
    }
    return ensureCustomerServiceBadge(phone) ? 1 : 0;
  };

  const resolveActualUnreadFallbackBadgeCount = (
    phone: string,
    visitingCustomerService: boolean
  ) => {
    if (!phone || visitingCustomerService) {
      return 0;
    }
    return consumeCustomerServiceBadgeOnce(phone) ? 1 : 0;
  };

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      mainRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  useEffect(() => {
    const phone = String(user?.phone || '').trim();
    if (!phone) {
      setCustomerServiceBadgeCount(0);
      setLastActualUnreadCount(0);
      return;
    }
  }, [user?.phone]);

  useEffect(() => {
    const phone = String(user?.phone || '').trim();
    const visitingCustomerService =
      location.pathname === '/customer-service' || location.pathname === '/service';
    const allowUnreadPolling = showNav;

    if (!phone) {
      setCustomerServiceBadgeCount(0);
      setLastActualUnreadCount(0);
      return;
    }

    if (appConfigLoading) {
      setCustomerServiceBadgeCount(0);
      return;
    }

    if (!appConfig.customerServiceUnreadCountEnabled) {
      setLastActualUnreadCount(0);
      setCustomerServiceBadgeCount(resolveFallbackBadgeCount(phone, visitingCustomerService));
      return;
    }

    if (!allowUnreadPolling) {
      if (visitingCustomerService) {
        setCustomerServiceBadgeCount(0);
        setLastActualUnreadCount(0);
      }
      return;
    }

    let cancelled = false;
    let timer: number | null = null;

    const refreshUnreadCount = async () => {
      try {
        const result = await fetchCustomerServiceUnreadCount(appConfig, user);
        if (cancelled) return;
        const unreadCount = Math.max(0, result.unread_count || 0);
        if (unreadCount > 0) {
          setLastActualUnreadCount(unreadCount);
          setCustomerServiceBadgeCount(visitingCustomerService ? 0 : unreadCount);
          return;
        }
        if (!visitingCustomerService && lastActualUnreadCount > 0) {
          setCustomerServiceBadgeCount(lastActualUnreadCount);
          return;
        }
        setLastActualUnreadCount(0);
        setCustomerServiceBadgeCount(
          resolveActualUnreadFallbackBadgeCount(phone, visitingCustomerService)
        );
      } catch {
        if (cancelled) return;
        if (!visitingCustomerService && lastActualUnreadCount > 0) {
          setCustomerServiceBadgeCount(lastActualUnreadCount);
          return;
        }
        setCustomerServiceBadgeCount(
          resolveActualUnreadFallbackBadgeCount(phone, visitingCustomerService)
        );
      }
    };

    const stopPolling = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    const startPolling = () => {
      stopPolling();
      if (document.visibilityState !== 'visible') {
        return;
      }
      void refreshUnreadCount();
      timer = window.setInterval(() => {
        void refreshUnreadCount();
      }, 3_000);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPolling();
        return;
      }
      stopPolling();
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [appConfig, appConfigLoading, lastActualUnreadCount, location.pathname, showNav, user?.phone, user?.uid]);

  return (
    <div className="app-shell flex min-h-0 flex-col relative overflow-x-hidden">
      <main ref={mainRef} className={`app-main min-h-0 flex-1 ${showNav ? 'app-main-with-nav' : ''}`}>
        {children}
      </main>

      {showNav && (
        <nav className="app-bottom-nav absolute bottom-0 left-0 right-0 flex items-stretch justify-between safe-area-bottom z-50">
          <NavItem icon={HomeIcon} label="首页" path="/" active={location.pathname === '/'} />
          <NavItem icon={Wallet} label="还款" path="/repayment" active={location.pathname === '/repayment'} />
          <NavItem
            icon={Headphones}
            label="客服"
            path="/customer-service"
            active={location.pathname === '/customer-service'}
            badgeCount={customerServiceBadgeCount}
          />
          <NavItem icon={User} label="我的" path="/user" active={location.pathname === '/user'} />
        </nav>
      )}
    </div>
  );
};

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { user, userLoading } = useAuthUser();
  if (userLoading) {
    return <PageLoadingState text="加载中..." />;
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
};

const AppConfigSessionRefresher = () => {
  const { user, userLoading } = useAuthUser();
  const { refresh } = useAppConfig();
  const lastAuthKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (userLoading) return;
    const authKey = user
      ? `user:${user.userId ?? ''}:${user.uid ?? ''}:${user.phone}`
      : 'guest';
    if (lastAuthKeyRef.current === authKey) return;
    lastAuthKeyRef.current = authKey;
    void refresh();
  }, [refresh, user?.phone, user?.uid, user?.userId, userLoading]);

  return null;
};

const App = () => {
  const { user, userLoading } = useAuthUser();

  return (
    <Layout>
      <AppConfigSessionRefresher />
      <Suspense fallback={<PageLoadingState text="加载中..." />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/user-agreement" element={<UserAgreement />} />
          <Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
          <Route path="/repayment" element={<RequireAuth><Repayment /></RequireAuth>} />
          <Route path="/customer-service" element={<RequireAuth><CustomerService /></RequireAuth>} />
          <Route path="/service" element={<Navigate to="/customer-service" replace />} />
          <Route path="/user" element={<RequireAuth><UserCenter /></RequireAuth>} />
          <Route path="/loan-records" element={<RequireAuth><LoanRecords /></RequireAuth>} />
          <Route path="/loan-details" element={<RequireAuth><LoanDetails /></RequireAuth>} />
          <Route path="/repayment-detail" element={<RequireAuth><RepaymentDetail /></RequireAuth>} />
          <Route path="/contract" element={<RequireAuth><Contract /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
          <Route path="/overdue-impact" element={<RequireAuth><OverdueImpact /></RequireAuth>} />
          <Route path="/upload-proof" element={<RequireAuth><UploadProof /></RequireAuth>} />
          <Route path="/delete-account" element={<RequireAuth><DeleteAccount /></RequireAuth>} />
          <Route path="/settlement-certificate" element={<RequireAuth><SettlementCertificate /></RequireAuth>} />
          <Route
            path="*"
            element={
              userLoading ? (
                <PageLoadingState text="加载中..." />
              ) : (
                <Navigate to={user ? '/' : '/login'} replace />
              )
            }
          />
        </Routes>
      </Suspense>
    </Layout>
  );
};

export default App;
